// ─────────────────────────────────────────────────────────────────────────────
//  UWC Design Tokens
//
//  Every scalable / themeable token is emitted as `var(--uwc-<token>, <fallback>)`
//  rather than a bare literal. This is the single mechanism that makes the entire
//  component library themeable AND scalable from the outside:
//
//    • A theme (data-uwc-theme="…") or the Theme Designer can override any global
//      token (`--uwc-font-size-md`, `--uwc-space-4`, `--uwc-radius-md`,
//      `--uwc-surface`, `--uwc-shadow-md`, …) at :root or on any subtree, and
//      EVERY component that consumes the token through `t.*` re-resolves instantly.
//    • The fallback after the comma is the default-theme value, so components keep
//      working with no theme stylesheet loaded.
//
//  Component-level tokens still take precedence — e.g. a component that writes
//  `var(--uwc-acc-header-font-size, ${t.fontSizeMd})` resolves to:
//      var(--uwc-acc-header-font-size, var(--uwc-font-size-md, 0.875rem))
//  i.e. component override → global scale token → literal fallback.
//
//  All values are pre-wrapped with unsafeCSS() so they can be interpolated
//  directly inside Lit css`` tagged templates. Values are hardcoded constants
//  (not user input) so unsafeCSS is safe here.
// ─────────────────────────────────────────────────────────────────────────────
import { unsafeCSS } from 'lit';

// ── Color primitives ─────────────────────────────────────────────────────────
// Driven by the global semantic color tokens so a palette change (theme or
// Theme Designer) cascades to every component.
export const primary   = unsafeCSS('var(--uwc-color-primary, #6366f1)');
export const secondary = unsafeCSS('var(--uwc-color-secondary, #64748b)');
export const success   = unsafeCSS('var(--uwc-color-success, #22c55e)');
export const warning   = unsafeCSS('var(--uwc-color-warning, #f59e0b)');
export const danger    = unsafeCSS('var(--uwc-color-danger, #ef4444)');
export const info      = unsafeCSS('var(--uwc-color-info, #38bdf8)');
export const help      = unsafeCSS('var(--uwc-color-help, #a855f7)');
export const contrast  = unsafeCSS('var(--uwc-color-contrast, #1e293b)');

// ── Surface & Text ────────────────────────────────────────────────────────────
// These are the DEFAULT-theme fallbacks every component uses (e.g.
// `var(--uwc-input-bg, ${t.surface})`). Each reads its global token first, then
// falls back to a light-dark() literal so that any component which neither themes
// its component-specific token nor sees a global override still adapts to dark
// mode. Values mirror the default theme's light/dark sides in
// src/styles/themes/default.css. light-dark() resolves against the element's
// inherited color-scheme, so it works the same inside every component shadow root.
export const surface         = unsafeCSS('var(--uwc-surface, light-dark(#ffffff, #18181b))');
export const surfaceRaised   = unsafeCSS('var(--uwc-surface-raised, light-dark(#f9f8f5, #232327))');
export const border          = unsafeCSS('var(--uwc-border, light-dark(rgba(0,0,0,0.09), rgba(255,255,255,0.12)))');
export const borderSubtle    = unsafeCSS('var(--uwc-border-subtle, light-dark(rgba(0,0,0,0.06), rgba(255,255,255,0.08)))');
// Visible border for form controls (inputs, selects, checkboxes etc.)
// Thicker than the subtle panel border so controls feel tactile.
export const borderControl   = unsafeCSS('var(--uwc-border-control, light-dark(rgba(0,0,0,0.20), rgba(255,255,255,0.26)))');
export const text            = unsafeCSS('var(--uwc-text, light-dark(#111111, #ededf0))');
export const textSecondary   = unsafeCSS('var(--uwc-text-secondary, light-dark(#64748b, #a1a1aa))');
export const textMuted       = unsafeCSS('var(--uwc-text-muted, light-dark(#9ca3af, #8b8b94))');
export const textDisabled    = unsafeCSS('var(--uwc-text-disabled, light-dark(#bbbbbb, #5b5b63))');
export const hoverBg         = unsafeCSS('var(--uwc-hover-bg, light-dark(rgba(0,0,0,0.04), rgba(255,255,255,0.06)))');
export const selectedBg      = unsafeCSS('var(--uwc-selected-bg, light-dark(rgba(99,102,241,0.08), rgba(129,140,248,0.22)))');
// Pure white — used as icon / checkmark / text colour on coloured backgrounds
// (stays white in both schemes; do NOT make this light-dark or themeable).
export const colorWhite      = unsafeCSS('#ffffff');

// ── Border radius ─────────────────────────────────────────────────────────────
export const radiusXs   = unsafeCSS('var(--uwc-radius-xs, 2px)');
export const radiusSm   = unsafeCSS('var(--uwc-radius-sm, 3px)');
export const radiusMd   = unsafeCSS('var(--uwc-radius-md, 6px)');
export const radiusLg   = unsafeCSS('var(--uwc-radius-lg, 12px)');
export const radiusXl   = unsafeCSS('var(--uwc-radius-xl, 18px)');
export const radiusFull = unsafeCSS('var(--uwc-radius-full, 9999px)');

// ── Spacing ───────────────────────────────────────────────────────────────────
// The density slider in the Theme Designer scales these proportionally, so every
// component that builds its padding / gap from space tokens reflows on the fly.
export const space1 = unsafeCSS('var(--uwc-space-1, 0.25rem)');
export const space2 = unsafeCSS('var(--uwc-space-2, 0.5rem)');
export const space3 = unsafeCSS('var(--uwc-space-3, 0.75rem)');
export const space4 = unsafeCSS('var(--uwc-space-4, 1rem)');
export const space5 = unsafeCSS('var(--uwc-space-5, 1.25rem)');
export const space6 = unsafeCSS('var(--uwc-space-6, 1.5rem)');

// ── Control heights ───────────────────────────────────────────────────────────
// THE single source of truth for the height of every form control — inputs,
// buttons, dropdown/autocomplete triggers, select buttons, toggle buttons,
// number inputs, date-picker triggers, option rows. Consuming this one token
// everywhere is what makes a button line up with an input at every size.
//
// The fallback is purely proportional to the base font size (×2.35 ≈ 33px at the
// default 0.875rem) so that simply bumping `--uwc-font-size-md` scales every
// control together. The Theme Designer / a theme can also pin an exact height by
// setting `--uwc-control-height-md`, which then drives the whole control family.
export const controlHeightMd = unsafeCSS(
  'var(--uwc-control-height-md, calc(var(--uwc-font-size-md, 0.875rem) * 2.35))'
);
// Small / large rungs of the SAME control-height ladder. They derive from
// `--uwc-control-height-md` (×0.8 / ×1.25), so the per-instance size attr stays
// proportional AND the whole family still rescales when the Theme Designer pins
// `--uwc-control-height-md`. A theme can also pin each rung individually via
// `--uwc-control-height-sm` / `--uwc-control-height-lg`.
export const controlHeightSm = unsafeCSS(
  'var(--uwc-control-height-sm, calc(var(--uwc-control-height-md, calc(var(--uwc-font-size-md, 0.875rem) * 2.35)) * 0.8))'
);
export const controlHeightLg = unsafeCSS(
  'var(--uwc-control-height-lg, calc(var(--uwc-control-height-md, calc(var(--uwc-font-size-md, 0.875rem) * 2.35)) * 1.25))'
);

// ── Typography ────────────────────────────────────────────────────────────────
export const fontSizeXs        = unsafeCSS('var(--uwc-font-size-xs, 0.6875rem)');
export const fontSizeSm        = unsafeCSS('var(--uwc-font-size-sm, 0.75rem)');
export const fontSizeMd        = unsafeCSS('var(--uwc-font-size-md, 0.875rem)');
export const fontSizeLg        = unsafeCSS('var(--uwc-font-size-lg, 1rem)');
export const fontSizeXl        = unsafeCSS('var(--uwc-font-size-xl, 1.125rem)');
export const fontWeightNormal   = unsafeCSS('var(--uwc-font-weight-normal, 400)');
export const fontWeightMedium   = unsafeCSS('var(--uwc-font-weight-medium, 500)');
export const fontWeightSemibold = unsafeCSS('var(--uwc-font-weight-semibold, 600)');
export const fontWeightBold     = unsafeCSS('var(--uwc-font-weight-bold, 700)');

// ── Icon sizes ──────────────────────────────────────────────────────────────
// Keyword icon sizes (consumed by uwc-icon's `size="xs|sm|md|lg|xl"`). Each is
// proportional to the base font size, so the Theme Designer's font scaling
// (which rewrites `--uwc-font-size-md`) flows straight through to icons — no
// separate icon-scaling wiring needed. A theme can still pin an exact px value
// via `--uwc-icon-size-md` etc. (Icons placed bare inside a control default to
// `1em` and instead track that control's own inherited font-size.)
export const iconSizeXs = unsafeCSS('var(--uwc-icon-size-xs, calc(var(--uwc-font-size-md, 0.875rem) * 0.85))');
export const iconSizeSm = unsafeCSS('var(--uwc-icon-size-sm, calc(var(--uwc-font-size-md, 0.875rem) * 1.0))');
export const iconSizeMd = unsafeCSS('var(--uwc-icon-size-md, calc(var(--uwc-font-size-md, 0.875rem) * 1.15))');
export const iconSizeLg = unsafeCSS('var(--uwc-icon-size-lg, calc(var(--uwc-font-size-md, 0.875rem) * 1.4))');
export const iconSizeXl = unsafeCSS('var(--uwc-icon-size-xl, calc(var(--uwc-font-size-md, 0.875rem) * 1.7))');

// ── Shadows ───────────────────────────────────────────────────────────────────
// The elevation preset in the Theme Designer overrides these globals; every
// component that consumes a shadow token re-elevates together.
export const shadowSm = unsafeCSS('var(--uwc-shadow-sm, 0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.10))');
export const shadowMd = unsafeCSS('var(--uwc-shadow-md, 0 4px 8px -2px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04))');
export const shadowLg = unsafeCSS('var(--uwc-shadow-lg, 0 1px 2px rgba(0,0,0,0.05), 0 4px 8px -2px rgba(0,0,0,0.08), 0 16px 32px -4px rgba(0,0,0,0.12))');

// ── Transitions ───────────────────────────────────────────────────────────────
export const durationFast   = unsafeCSS('var(--uwc-duration-fast, 80ms)');
export const durationBase   = unsafeCSS('var(--uwc-duration-base, 120ms)');
export const durationslow   = unsafeCSS('var(--uwc-duration-slow, 200ms)');
export const easingStandard = unsafeCSS('var(--uwc-easing, cubic-bezier(0.16, 1, 0.3, 1))');
export const easingOut      = unsafeCSS('var(--uwc-easing-out, cubic-bezier(0, 0, 0.2, 1))');

// ── Z-index ───────────────────────────────────────────────────────────────────
export const zFloat   = unsafeCSS('9999');
export const zTooltip = unsafeCSS('10000');
export const zDialog  = unsafeCSS('10001');
